import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation
} from '@angular/core';

import { ActivatedRoute, NavigationStart, Params, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { PushToGoogleTagManager } from '../../utility/helpers/google-tag-manager';
import { getDefaultIconNameForPage } from '../../utility/helpers/helpers';
import { IBaseContentJson } from '../../utility/interfaces/page';
import { PageVM } from '../../utility/models/page-vm';
import { CommunityService } from '../../utility/services/community.service';
import { NavigationService, PageTitle } from '../../utility/services/navigation.service';
import { PageService } from '../../utility/services/page.service';
import { SettingsService } from '../../utility/services/settings.service';
import { ThemeService } from '../../utility/services/theme.service';

@Component({
  selector: 'slide-out-menu',
  templateUrl: './slide-out-menu.component.html',
  styleUrls: ['./slide-out-menu.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class SlideOutMenuComponent implements OnInit, OnDestroy {
  @Input() pageTitle: PageTitle;
  @Input() heading: string;
  @Input() iconName: string;
  @Input() isOpen = true;
  @Input() isPullTabAtBottom = false;
  @Input() useAltOpenButton = false; // need better name
  @Input() doStopClickPropagation = true;
  @Input() hasLeftOrigin = false;
  @Input() hasFavoriteButton = false;
  @Input() isFavorited = false;
  @Input() showTouchHint = false;
  @Input() hideCommunityNameInStandalone = false;
  @Output() toggle = new EventEmitter<boolean>();
  @Output() favorite = new EventEmitter<boolean>();
  iconSvg: string;
  showIconBackground = true;
  showHeaderIcon = true;
  private routerSubscription: Subscription;

  constructor(
    private pageService: PageService,
    private settingsService: SettingsService,
    private theme: ThemeService,
    private navigator: NavigationService,
    private communityService: CommunityService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit() {
    let prevUrl = this.router.url.split('?')[0];
    this.routerSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        const currUrl = event.url.split('?')[0];

        if (prevUrl !== currUrl && !currUrl.includes('(')) {
          this.cleanupBeforeNavigation();
          prevUrl = currUrl;
        }
      }
    });

    this.route.params.subscribe((params: Params) => {
      if (this.pageTitle) {
        const page = this.getPage(+params.pageId);

        if (!this.heading) {
          const pageTitle = page.NavigationTitle || page.PageTitle || this.pageTitle;

          if (this.navigator.IsInStandaloneMode && !this.hideCommunityNameInStandalone) {
            this.heading = `${this.communityService.getDisplayName()}<br/> <span>${pageTitle}</span>`;
          } else {
            this.heading = pageTitle;
          }
        }

        if (!this.iconName) {
          this.iconName = getDefaultIconNameForPage(
            page.NavigationTitle || page.PageTitle || this.pageTitle
          );

          this.iconSvg = page.IconSvgContent;
        }
        this.showIconBackground = this.settingsService.get('ShowHeaderIconBkgrnd');
        this.showHeaderIcon =
          !!this.settingsService.get('HeaderIconShow') && (!!this.iconName || !!this.iconSvg);
      }
    });
  }

  private getPage(pageId: number) {
    let page: PageVM<IBaseContentJson>;

    if (pageId) {
      page = this.pageService.getById(pageId);
    }

    if (!page) {
      page = this.pageService.get(this.pageTitle);
    }

    return page;
  }

  toggleMenu(evt: Event) {
    evt.stopPropagation();
    this.isOpen = !this.isOpen;
    this.theme.updateSlideOutMenuCurrentWidth(this.isOpen);
    this.toggle.emit(this.isOpen);

    PushToGoogleTagManager({
      eventName: 'ToggleSideMenuOpenClosed',
      eventType: 'App Click Interaction',
      eventValue: `${this.isOpen}`
    });
  }

  maybeBlockEventPropagation(evt: Event) {
    if (this.doStopClickPropagation) evt.stopPropagation();
  }

  handleFavoriteToggle() {
    this.isFavorited = !this.isFavorited;
    this.favorite.emit(this.isFavorited);
  }

  cleanupBeforeNavigation() {
    this.heading = '';
    this.iconName = '';
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }
}
